import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { normSearch } from "@/lib/utils";
import { PasswordField } from "@/components/Atoms";
import { useBackdropClose } from "@/lib/useBackdropClose";
import { Plus, MagnifyingGlass, Pencil, Key, LockOpen, Lock, ShieldCheck } from "@phosphor-icons/react";

const ROLES = [
    { code: "supervisor", label: "Supervisor", color: "#14B8A6" },
    { code: "comercial",  label: "Comercial",  color: "#2563EB" },
    { code: "tecnico",    label: "Técnico",    color: "#0A191E" },
    { code: "cliente",    label: "Cliente",    color: "#92400E" },
];

const ESPECIALIDADES = ["Electrico Industrial", "HVAC", "Hidraulico", "Estructural", "Maestro de Obra", "General"];

export default function AdminUsers() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [roleFilter, setRoleFilter] = useState("todos");
    const [showCreate, setShowCreate] = useState(false);
    const [editing, setEditing] = useState(null);
    const [resetting, setResetting] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/users");
            setItems(r.data);
        } catch (e) { setError(e.response?.data?.detail || "Error cargando usuarios"); }
        setLoading(false);
    };
    useEffect(() => { if (user?.is_admin) load(); }, [user?.is_admin]);

    const counts = useMemo(() => {
        const c = { todos: items.length };
        ROLES.forEach(r => { c[r.code] = items.filter(u => u.role === r.code).length; });
        return c;
    }, [items]);

    if (!user) return null;
    if (!user.is_admin) return <Navigate to="/dashboard" replace />;

    const filtered = items.filter(u => {
        if (roleFilter !== "todos" && u.role !== roleFilter) return false;
        if (!q) return true;
        const s = normSearch(q);
        return [u.name, u.cedula, u.email, u.telefono].some(v => normSearch(v).includes(s));
    });

    const toggleActive = async (u) => {
        try {
            await api.patch(`/admin/users/${u.user_id}`, { activo: !u.activo });
            load();
        } catch (e) { alert(e.response?.data?.detail || "Error"); }
    };

    return (
        <div className="max-w-7xl mx-auto p-4 md:p-8" data-testid="admin-users-page">
            <div className="flex items-end justify-between mb-6 border-b border-zinc-200 pb-6 flex-wrap gap-4">
                <div>
                    <div className="overline mb-2 flex items-center gap-2">
                        <ShieldCheck size={14} weight="fill" style={{ color: "#B45309" }} />
                        Módulo administrador · Sólo cuenta principal
                    </div>
                    <h1 className="font-display text-4xl font-black tracking-tighter">Gestión de usuarios</h1>
                    <p className="text-sm text-zinc-600 mt-1">Crea, edita, bloquea y resetea contraseñas de cualquier usuario del sistema.</p>
                </div>
                <button onClick={() => setShowCreate(true)} className="btn-accent flex items-center gap-2" data-testid="new-user-btn">
                    <Plus size={16} weight="bold" /> Nuevo usuario
                </button>
            </div>

            {/* Tabs por rol */}
            <div className="flex flex-wrap gap-2 mb-4" data-testid="role-tabs">
                <TabBtn code="todos" label="Todos" count={counts.todos} active={roleFilter === "todos"} onClick={() => setRoleFilter("todos")} color="#0F172A" />
                {ROLES.map(r => (
                    <TabBtn key={r.code} code={r.code} label={r.label} count={counts[r.code]} active={roleFilter === r.code} onClick={() => setRoleFilter(r.code)} color={r.color} />
                ))}
            </div>

            {/* Buscador */}
            <div className="relative mb-4 max-w-md">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input type="text" placeholder="Buscar por nombre, cédula, email…" className="input-atia pl-9" value={q} onChange={e => setQ(e.target.value)} data-testid="search-input" />
            </div>

            {/* Tabla */}
            <div className="border border-zinc-200 overflow-x-auto">
                <table className="atia-table" data-testid="users-table">
                    <thead><tr><th>Nombre</th><th>Cédula</th><th>Rol</th><th>Email</th><th>Teléfono</th><th>Estado</th><th style={{ width: 220 }}>Acciones</th></tr></thead>
                    <tbody>
                        {loading && <tr><td colSpan={7} className="text-center py-8 text-zinc-500">Cargando…</td></tr>}
                        {!loading && filtered.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-zinc-500">Sin usuarios en este filtro.</td></tr>}
                        {filtered.map(u => {
                            const roleMeta = ROLES.find(r => r.code === u.role);
                            return (
                                <tr key={u.user_id} data-testid={`user-row-${u.cedula || u.user_id}`}>
                                    <td className="font-semibold">
                                        {u.name}
                                        {u.is_admin && <span className="ml-2 text-[10px] font-mono font-bold px-1.5 py-0.5 border border-amber-500 bg-amber-50 text-amber-800">ADMIN</span>}
                                    </td>
                                    <td className="font-mono text-xs">{u.cedula || "—"}</td>
                                    <td>
                                        <span className="text-[10px] font-mono font-bold px-2 py-0.5 border" style={{ borderColor: roleMeta?.color || "#666", color: roleMeta?.color || "#666", background: (roleMeta?.color || "#666") + "10" }}>
                                            {(roleMeta?.label || u.role).toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="text-xs">{u.email}</td>
                                    <td className="font-mono text-xs">{u.telefono || "—"}</td>
                                    <td>
                                        {u.activo ? (
                                            <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-emerald-500 bg-emerald-50 text-emerald-800">ACTIVO</span>
                                        ) : (
                                            <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-red-500 bg-red-50 text-red-800">BLOQUEADO</span>
                                        )}
                                    </td>
                                    <td>
                                        <div className="flex gap-1">
                                            <button onClick={() => setEditing(u)} className="p-1.5 border border-zinc-200 hover:border-teal-500 hover:bg-teal-50" title="Editar" data-testid={`edit-user-${u.cedula || u.user_id}`}>
                                                <Pencil size={14} />
                                            </button>
                                            <button onClick={() => setResetting(u)} className="p-1.5 border border-zinc-200 hover:border-teal-500 hover:bg-teal-50" title="Resetear contraseña" data-testid={`reset-pw-${u.cedula || u.user_id}`}>
                                                <Key size={14} />
                                            </button>
                                            {u.is_admin ? (
                                                <button className="p-1.5 border border-zinc-200 opacity-40 cursor-not-allowed" title="No se puede bloquear al admin principal" disabled>
                                                    <Lock size={14} />
                                                </button>
                                            ) : (
                                                <button onClick={() => toggleActive(u)} className="p-1.5 border border-zinc-200 hover:border-red-500 hover:bg-red-50" title={u.activo ? "Bloquear" : "Desbloquear"} data-testid={`toggle-user-${u.cedula || u.user_id}`}>
                                                    {u.activo ? <Lock size={14} /> : <LockOpen size={14} style={{ color: "#16A34A" }} />}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showCreate && <UserFormModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
            {editing && <UserFormModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
            {resetting && <PasswordResetModal user={resetting} onClose={() => setResetting(null)} onDone={() => setResetting(null)} />}
        </div>
    );
}

function TabBtn({ code, label, count, active, onClick, color }) {
    return (
        <button onClick={onClick} className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border transition-all ${active ? "text-white" : "text-zinc-700 bg-white hover:bg-zinc-50"}`}
            style={active ? { background: color, borderColor: color } : { borderColor: "#E4E4E7" }}
            data-testid={`tab-role-${code}`}>
            {label} <span className="font-mono ml-1">({count})</span>
        </button>
    );
}

function UserFormModal({ user, onClose, onSaved }) {
    const isEdit = !!user;
    const backdrop = useBackdropClose(onClose);
    const [form, setForm] = useState(
        isEdit
            ? { name: user.name || "", email: user.email || "", telefono: user.telefono || "", role: user.role || "tecnico" }
            : { name: "", cedula: "", email: "", telefono: "", role: "comercial", password: "", client_id: "", especialidad: "General" }
    );
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!isEdit && form.role === "cliente") {
            api.get("/clients").then(r => setClients(r.data)).catch(() => setClients([]));
        }
    }, [form.role, isEdit]);

    const submit = async (e) => {
        e.preventDefault(); setError(""); setLoading(true);
        try {
            if (isEdit) {
                await api.patch(`/admin/users/${user.user_id}`, form);
            } else {
                if ((form.password || "").length < 6) throw { response: { data: { detail: "La contraseña debe tener al menos 6 caracteres" } } };
                const payload = { ...form };
                if (payload.role !== "cliente") delete payload.client_id;
                if (payload.role !== "tecnico") delete payload.especialidad;
                if (!payload.telefono) payload.telefono = null;
                await api.post("/admin/users", payload);
            }
            onSaved();
        } catch (err) { setError(err.response?.data?.detail || "Error"); setLoading(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" {...backdrop.backdropProps} data-testid="user-form-modal">
            <form onSubmit={submit} className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" {...backdrop.contentProps}>
                <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
                    <div>
                        <div className="overline mb-1" style={{ color: "#0F766E" }}>{isEdit ? "Editar usuario" : "Crear usuario"}</div>
                        <h2 className="font-display text-3xl font-black tracking-tighter">{isEdit ? user.name : "Nuevo usuario"}</h2>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-3xl leading-none">×</button>
                </div>
                <div className="p-6 grid md:grid-cols-2 gap-4">
                    <Field label="Nombre completo *"><input required className="input-atia" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="input-name" /></Field>
                    {!isEdit && <Field label="Cédula / Identificador *"><input required className="input-atia font-mono" value={form.cedula} onChange={e => setForm({ ...form, cedula: e.target.value })} data-testid="input-cedula" /></Field>}
                    <Field label="Email *"><input required type="email" className="input-atia" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="input-email" /></Field>
                    <Field label="Teléfono"><input className="input-atia font-mono" value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} data-testid="input-phone" /></Field>
                    <Field label="Rol *">
                        <select className="input-atia" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} data-testid="input-role">
                            {ROLES.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                        </select>
                    </Field>
                    {!isEdit && form.role === "tecnico" && (
                        <Field label="Especialidad">
                            <select className="input-atia" value={form.especialidad} onChange={e => setForm({ ...form, especialidad: e.target.value })} data-testid="input-especialidad">
                                {ESPECIALIDADES.map(e => <option key={e}>{e}</option>)}
                            </select>
                        </Field>
                    )}
                    {!isEdit && form.role === "cliente" && (
                        <Field label="Cliente asociado *">
                            <select required className="input-atia" value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })} data-testid="input-client-id">
                                <option value="">Selecciona…</option>
                                {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.razon_social} · {c.nit}</option>)}
                            </select>
                        </Field>
                    )}
                    {!isEdit && (
                        <Field label="Contraseña inicial *">
                            <PasswordField value={form.password} onChange={v => setForm({ ...form, password: v })} testid="input-password" autoComplete="new-password" required />
                        </Field>
                    )}
                </div>
                {error && <div className="mx-6 mb-4 p-3 text-sm border border-red-300 bg-red-50 text-red-900" data-testid="form-error">{error}</div>}
                <div className="p-6 border-t border-zinc-200 flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
                    <button type="submit" disabled={loading} className="btn-accent" data-testid="submit-user-form">
                        {loading ? "Guardando…" : isEdit ? "Guardar cambios →" : "Crear usuario →"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function PasswordResetModal({ user, onClose, onDone }) {
    const backdrop = useBackdropClose(onClose);
    const [pw, setPw] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault(); setError("");
        if (pw.length < 6) { setError("Mínimo 6 caracteres"); return; }
        setLoading(true);
        try {
            await api.post(`/admin/users/${user.user_id}/reset-password`, { new_password: pw });
            alert(`Contraseña de ${user.name} actualizada. Sus sesiones activas fueron cerradas.`);
            onDone();
        } catch (e) { setError(e.response?.data?.detail || "Error"); setLoading(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" {...backdrop.backdropProps} data-testid="reset-password-modal">
            <form onSubmit={submit} className="bg-white max-w-md w-full" {...backdrop.contentProps}>
                <div className="p-6 border-b border-zinc-200">
                    <div className="overline mb-1" style={{ color: "#0F766E" }}>Resetear contraseña</div>
                    <h3 className="font-display text-2xl font-bold tracking-tight">{user.name}</h3>
                    <p className="text-xs text-zinc-500 mt-1">Al confirmar, se cerrarán todas las sesiones activas de este usuario.</p>
                </div>
                <div className="p-6">
                    <div className="overline mb-1.5">Nueva contraseña</div>
                    <PasswordField value={pw} onChange={setPw} testid="new-password" autoComplete="new-password" />
                </div>
                {error && <div className="mx-6 mb-4 p-3 text-sm border border-red-300 bg-red-50 text-red-900">{error}</div>}
                <div className="p-6 border-t border-zinc-200 flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
                    <button type="submit" disabled={loading} className="btn-accent" data-testid="submit-reset">{loading ? "Guardando…" : "Actualizar →"}</button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, children }) {
    return <label className="block"><div className="overline mb-1.5">{label}</div>{children}</label>;
}
